/**
 * @file Escalations.tsx
 * @deprecated Copied to app/modules/procurement/pages/escalations/Escalations.tsx. This file still serves the existing /UProcure routes.
 * @module app/modules/uProcure/escalations/Escalations
 * @description Escalations list view with tabbed interface for status filtering (Pending, 
 *   Resolved, Rejected). Displays paginated table with search, date filtering, and navigation 
 *   to detail views. Integrates with ESCALATIONS API endpoints for data fetching.
 * 
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo [P1] Implement API data fetching:
 *   - Replace static tab data with fetchEscalations() API call
 *   - Support status filtering per tab (Pending/Resolved/Rejected)
 *   - Implement pagination (page, limit, total)
 *   - Add search functionality via debounced API call
 *   - Reference: uprocure admin escalation list implementation
 * 
 * @todo [P2] Add date range filtering
 *   - Implement handleDateFilter() for raised date range
 *   - Store dates in session storage via getKey/setKey pattern
 *   - Re-fetch list on date change
 *   - Reference: purchase-order-list.tsx date filter logic
 * 
 * @todo [P2] Add column definitions for react-table
 *   - Escalation ID (refKey)
 *   - Type (Pending Approval, Delivery Delay, Payment Delay)
 *   - Reason/Description
 *   - Raised By (user name)
 *   - Created Date
 *   - Status badge (color-coded)
 *   - Actions (view details, edit if pending)
 * 
 * @todo [P3] Add error handling and retry logic
 *   - Try/catch blocks around fetchEscalations
 *   - Show error toasts for API failures
 *   - Reset loading flags in finally blocks
 * 
 * @todo [P3] Implement escalation type filter (optional)
 *   - Add dropdown to filter by type (Pending Approval, Delivery Delay, etc.)
 *   - Similar to category filter in PO list
 */

/* eslint-disable prefer-const */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import DynamicListLayout from "../../../custom/layouts/DynamicListLayout";
import { CustomSearchInput } from "../../../custom/search";
import { CustomTable } from "../../../custom/data-table";
import APICallService from "../../../../api/apiCallService";
import { ColumnDef } from "@tanstack/react-table";
import { ESCALATIONS } from "../../../../api/apiEndPoints";
import { useDebounce } from "../../../../utils/useDebounce";
import { getKey, setKey } from "../../../../Global/history";
import { listEscalations } from "../../../../utils/storeString";
import { useNavigationHistory } from "../../../../utils/useNavigationHistory";

const escalationTypeStatusMeta: any = {
    1: { label: "Pending Approval" },
    2: { label: "Delivery Delay" },
    3: { label: "Payment Delay" },
};

/* =======================
   Escalation Status Enum
======================= */
export const EscalationStatus = {
    Pending: 1,
    Resolved: 2,
    Rejected: 3,
} as const;

interface IEscalation {
    _id: string;
    refKey: string;
    raisedBy?: {
        name?: string;
    };
    type: number;
    reason?: string;
    shipment?: string;
    status?: number;
}

/* =======================
   Tabs Config
======================= */
const tabs = [
    { label: "Pending", value: EscalationStatus.Pending },
    { label: "Resolved", value: EscalationStatus.Resolved },
    { label: "Rejected", value: EscalationStatus.Rejected },
];

/* =======================
   Component
======================= */
const Escalations: React.FC = () => {
    
    const { navigateTo } = useNavigationHistory();
    const [loading, setLoading] = useState(false);
    const [escalations, setEscalations] = useState<any[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const [page, setPage] = useState<number>(() => {
        const savedPage = getKey(listEscalations.page);
        return savedPage ? parseInt(savedPage as string, 10) : 1;
    });
    const [pageLimit, setPageLimit] = useState<number>(() => {
        const savedLimit = getKey(listEscalations.limit);
        return savedLimit ? parseInt(savedLimit as string, 10) : 10;
    });
    const [activeTab, setActiveTab] = useState<number>(() => {
        const savedTab = getKey(listEscalations.currentTab);
        return savedTab
            ? parseInt(savedTab as string, 10)
            : EscalationStatus.Pending;
    });
    const [search, setSearch] = useState<string | any>(() => {
        const savedSearch = getKey(listEscalations.search);
        return savedSearch || "";
    });

    /* =======================
       Table Columns
    ======================= */
    const columns: ColumnDef<IEscalation>[] = [
        {
            header: "Escalation Ref",
            accessorKey: "refKey",
        },
        {
            header: "Escalation Type",
            cell: ({ row }) => escalationTypeStatusMeta[row.original.type || 1]?.label,
        },
        {
            header: "Raised By",
            cell: ({ row }) => row.original.raisedBy?.name || "-",
        },

        {
            header: "Reason",
            cell: ({ row }) => row.original.reason || "",
        },
    ];

    /* =======================
       Row Click Navigation
    ======================= */
    const goToEscalationDetails = (escalation: IEscalation) => {
        let currentScrollY = window.scrollY;
        setKey(listEscalations.scrollPosition, currentScrollY);
        setKey(listEscalations.search, search, false);
        setKey(listEscalations.page, page, false);
        setKey(listEscalations.currentTab, activeTab, false);
        navigateTo('/UProcure/escalations/escalation-details', {
            state: { id: escalation._id }
        });
    };

    /* =======================
       API Call
    ======================= */
    const fetchEscalations = async (
        pageNo: number,
        limit: number,
        status?: number,
        searchText?: string
    ) => {
        setLoading(true);

        let params: any = {
            pageNo: pageNo,
            limit: limit,
            sortKey: "createdAt",
            sortOrder: -1,
            needCount: true,
            searchTerm: searchText || "",
        };

        // Status handling (future-proof like PO)
        if (status !== undefined && status !== null) {
            params.status = status;
        }

        const apiService = new APICallService(ESCALATIONS.listEscalation, params);

        let response = await apiService.callAPI();

        if (response?.records) {
            if (response?.total) {
                setTotalRecords(response.total);
            }
            setEscalations(response.records);
        } else {
            setEscalations([]);
        }

        setLoading(false);
    };

    const debounce = useDebounce(fetchEscalations, 300);

    const handleSearch = async (input: string) => {
        setSearch(input);
        setPage(1);
        setKey(listEscalations.page, 1);
        setKey(listEscalations.search, input, false);

        await debounce(1, pageLimit, activeTab, input);
    };

    useEffect(() => {
        fetchEscalations(page, pageLimit, activeTab, search);
    }, []);

    /* =======================
       Pagination Handlers
    ======================= */
    const handleCurrentPage = async (val: number) => {
        if (val === page || val.toString() === "...") return;

        setPage(val);
        setKey(listEscalations.page, val);
        await fetchEscalations(val, pageLimit, activeTab, search);
    };

    const handleNextPage = async (val: number) => {
        const nextPage = val + 1;

        setPage(nextPage);
        setKey(listEscalations.page, nextPage);
        await fetchEscalations(nextPage, pageLimit, activeTab, search);
    };

    const handlePreviousPage = async (val: number) => {
        const prevPage = val - 1;

        if (prevPage < 1) return;

        setPage(prevPage);
        setKey(listEscalations.page, prevPage);
        await fetchEscalations(prevPage, pageLimit, activeTab, search);
    };

    const handlePageLimit = async (event: any) => {
        const limit = Number(event.target.value);

        setPage(1);
        setPageLimit(limit);
        setKey(listEscalations.page, 1);
        setKey(listEscalations.limit, limit);

        await fetchEscalations(1, limit, activeTab, search);
    };

    const handleTabChange = async (value: string | number) => {
        const numericValue =
            typeof value === "string" ? parseInt(value, 10) : value;

        setActiveTab(numericValue);
        setPage(1);
        setKey(listEscalations.currentTab, numericValue);
        setKey(listEscalations.page, 1);

        await fetchEscalations(1, pageLimit, numericValue, search);
    };

    /* =======================
       Render
    ======================= */
    return (
        <DynamicListLayout
            title="Escalations"
            variant="withTabs"
            activeTab={activeTab}
            onTabChange={handleTabChange}
            tabs={tabs}
            page={page}
            pageLimit={pageLimit}
            totalRecords={totalRecords}
            handleCurrentPage={handleCurrentPage}
            handleNextPage={handleNextPage}
            handlePreviousPage={handlePreviousPage}
            handlePageLimit={handlePageLimit}
            filters={
                <div className="min-w-280px max-w-280px">
                    <CustomSearchInput
                        placeholder="Search escalation"
                        defaultValue={search}
                        onSearch={(value) => handleSearch(value)}
                    />
                </div>
            }
        >
            <CustomTable 
                data={escalations} 
                columns={columns} 
                loading={loading}
                onRowClick={(escalation: IEscalation) => {
                    goToEscalationDetails(escalation);
                }}
            />
        </DynamicListLayout>
    );
};

export default Escalations;
